import { useCallback, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'next-i18next'

import {
  fetchLegacyCoinLockersApi,
  FetchLegacyCoinLockersParams,
} from 'features/coin-locker/services/fetchLegacyCoinLockersApi'
import type { LegacyCoinLocker } from 'features/coin-locker/types/legacyCoinLocker'

export type UseLegacyCoinLockersParams = Omit<FetchLegacyCoinLockersParams, 'locale'>

export const useLegacyCoinLockers = () => {
  const { i18n } = useTranslation()
  const [legacyCoinLockers, setLegacyCoinLockers] = useState<LegacyCoinLocker[]>([])
  const [isLoading, setLoading] = useState(false)

  const fetchLegacyCoinLockers = useCallback(
    async (params: UseLegacyCoinLockersParams) => {
      setLoading(true)

      try {
        const data = await fetchLegacyCoinLockersApi({
          ...params,
          locale: i18n.language,
        })
        setLegacyCoinLockers(data.coin_lockers)
      } catch (error) {
        setLegacyCoinLockers([])
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
          tags: { hook: 'useLegacyCoinLockers' },
          extra: { locale: i18n.language, ...params },
        })
      } finally {
        setLoading(false)
      }
    },
    [i18n.language]
  )

  return {
    legacyCoinLockers,
    isLoading,
    fetchLegacyCoinLockers,
  }
}
